import { useRecoverableRead } from '../hooks/useRecoverableRead';
import ReadRecoveryNotice from '../components/ReadRecoveryNotice';
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const CarteraContext = createContext();

export const useCartera = () => {
  const context = useContext(CarteraContext);
  if (!context) {
    throw new Error('useCartera must be used within a CarteraProvider');
  }
  return context;
};

// Mantener export del hook antiguo para compatibilidad
export const usePortfolio = useCartera;

export const CarteraProvider = ({ children }) => {
  const [carteras, setCarteras] = useState([]);
  const [activeCartera, setActiveCartera] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const portfolioRead = useRecoverableRead(token);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchCarteras = useCallback(async () => {
    if (!token) return;
    
    // Workers sin permiso de movimientos no necesitan carteras
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        if (userData.role === 'worker' && !userData.permissions?.includes('movimientos')) {
          setLoading(false);
          return;
        }
      }
    } catch (e) {}
    
    try {
      const response = await portfolioRead.get(`${BACKEND_URL}/api/carteras`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      {
        const data = response.data;
        setCarteras(data);
        
        // Si no hay cartera activa y hay carteras disponibles, seleccionar la primera
        const savedCarteraId = localStorage.getItem('activeCarteraId');
        const hasActiveCartera = savedCarteraId !== null;
        
        if (!hasActiveCartera && data.length > 0) {
          const cartera = data[0];
          setActiveCartera(cartera);
          localStorage.setItem('activeCarteraId', cartera.id);
        } else if (hasActiveCartera && data.length > 0) {
          // Verificar si la cartera guardada aún existe
          const savedCartera = data.find(c => c.id === savedCarteraId);
          if (savedCartera) {
            setActiveCartera(savedCartera);
          } else {
            // Si la cartera guardada no existe, usar la primera
            setActiveCartera(data[0]);
            localStorage.setItem('activeCarteraId', data[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching carteras:', error);
    } finally {
      setLoading(false);
    }
  }, [token, BACKEND_URL, portfolioRead.get]);

  useEffect(() => {
    fetchCarteras();
  }, [fetchCarteras]);

  const createCartera = async (carteraData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/carteras`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(carteraData)
      });

      if (response.ok) {
        const newCartera = await response.json();
        setCarteras([...carteras, newCartera]);
        
        // Si es la primera cartera, hacerla activa
        if (carteras.length === 0) {
          setActiveCartera(newCartera);
          localStorage.setItem('activeCarteraId', newCartera.id);
        }
        
        return newCartera;
      } else {
        throw new Error('Failed to create cartera');
      }
    } catch (error) {
      console.error('Error creating cartera:', error);
      throw error;
    }
  };

  const updateCartera = async (carteraId, carteraData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/carteras/${carteraId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(carteraData)
      });

      if (response.ok) {
        const updatedCartera = await response.json();
        setCarteras(carteras.map(c => c.id === carteraId ? updatedCartera : c));
        
        // Si es la cartera activa, actualizarla
        if (activeCartera?.id === carteraId) {
          setActiveCartera(updatedCartera);
        }
        
        return updatedCartera;
      } else {
        throw new Error('Failed to update cartera');
      }
    } catch (error) {
      console.error('Error updating cartera:', error);
      throw error;
    }
  };

  const deleteCartera = async (carteraId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/carteras/${carteraId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const remainingCarteras = carteras.filter(c => c.id !== carteraId);
        setCarteras(remainingCarteras);
        
        // Si se eliminó la cartera activa, seleccionar otra
        if (activeCartera?.id === carteraId) {
          if (remainingCarteras.length > 0) {
            setActiveCartera(remainingCarteras[0]);
            localStorage.setItem('activeCarteraId', remainingCarteras[0].id);
          } else {
            setActiveCartera(null);
            localStorage.removeItem('activeCarteraId');
          }
        }
        
        return true;
      } else {
        throw new Error('Failed to delete cartera');
      }
    } catch (error) {
      console.error('Error deleting cartera:', error);
      throw error;
    }
  };

  const switchCartera = (cartera) => {
    setActiveCartera(cartera);
    localStorage.setItem('activeCarteraId', cartera.id);
  };

  const value = {
    carteras,
    activeCartera,
    loading,
    fetchCarteras,
    createCartera,
    updateCartera,
    deleteCartera,
    switchCartera,
    // Aliases para compatibilidad
    portfolios: carteras,
    activePortfolio: activeCartera,
    fetchPortfolios: fetchCarteras,
    createPortfolio: createCartera,
    updatePortfolio: updateCartera,
    deletePortfolio: deleteCartera,
    switchPortfolio: switchCartera
  };

  return (
    <CarteraContext.Provider value={value}>
      <ReadRecoveryNotice reads={[portfolioRead]} />
      {children}
    </CarteraContext.Provider>
  );
};

// Export con nombre antiguo para compatibilidad
export const PortfolioProvider = CarteraProvider;

// Export default para compatibilidad con imports sin nombre
export default CarteraProvider;
